import { useParams, useNavigate } from 'react-router-dom'
import { useKnowledgeStore } from '../store/knowledgeStore'
import { ArrowLeft, ThumbsUp, ThumbsDown, Share2, Edit, Trash2 } from 'lucide-react'

export default function KnowledgeDetailPage() {
  const { docId } = useParams()
  const navigate = useNavigate()
  const getDocument = useKnowledgeStore((state) => state.getDocument)
  const markHelpful = useKnowledgeStore((state) => state.markHelpful)
  const markUnhelpful = useKnowledgeStore((state) => state.markUnhelpful)

  const doc = getDocument(docId)

  if (!doc) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-blue-300/70">Article not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/knowledge-base')}
          className="flex items-center space-x-2 text-cyber-600 hover:text-cyber-700 font-medium"
        >
          <ArrowLeft size={20} />
          <span>Back to Knowledge Base</span>
        </button>
        <div className="flex items-center space-x-2">
          <button className="btn-secondary py-2 px-3">
            <Edit size={18} />
          </button>
          <button className="btn-danger py-2 px-3">
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-xs bg-slate-700/20 px-3 py-1 rounded capitalize text-blue-200 font-medium">
                  {doc.category}
                </span>
                <span className="text-xs text-blue-300/70">
                  Updated {new Date(doc.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">{doc.title}</h1>
              <div className="prose prose-sm max-w-none text-blue-200 leading-relaxed">
                <p>{doc.content}</p>
              </div>

              {/* Feedback */}
              <div className="mt-8 pt-6 border-t border-blue-500/30">
                <p className="text-sm font-medium text-blue-200 mb-3">Was this article helpful?</p>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => markHelpful(docId)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <ThumbsUp size={18} />
                    <span className="text-sm font-medium">Yes ({doc.helpful})</span>
                  </button>
                  <button
                    onClick={() => markUnhelpful(docId)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                  >
                    <ThumbsDown size={18} />
                    <span className="text-sm font-medium">No ({doc.unhelpful})</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Statistics</h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <p className="text-sm text-blue-200">Views</p>
                <p className="text-2xl font-bold text-white">{doc.views}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Helpful</p>
                <p className="text-2xl font-bold text-green-600">{doc.helpful}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Unhelpful</p>
                <p className="text-2xl font-bold text-red-600">{doc.unhelpful}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Satisfaction</p>
                <p className="text-2xl font-bold text-cyber-600">
                  {doc.helpful + doc.unhelpful > 0
                    ? Math.round((doc.helpful / (doc.helpful + doc.unhelpful)) * 100)
                    : 0}%
                </p>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Information</h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <p className="text-sm text-blue-200">Author</p>
                <p className="font-medium text-white">{doc.author}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Created</p>
                <p className="font-medium text-white">{new Date(doc.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Last Updated</p>
                <p className="font-medium text-white">{new Date(doc.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Share */}
          <button className="btn-secondary w-full justify-center flex items-center space-x-2">
            <Share2 size={20} />
            <span>Share Article</span>
          </button>
        </div>
      </div>
    </div>
  )
}
